import { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "src/context/AuthProvider";
import { useApi } from "src/hooks/useApi";
import { useSettings, DEFAULT_PDF_SETTINGS } from "./useSettings";
import { apiRequest } from "src/services/apiClient";
import { canSeeBank as canSeeBankFor } from "src/utils/roles";
import { useTranslation } from "react-i18next";
import { MdOutlineLanguage, MdOutlineLocalPostOffice, MdOutlineMailOutline, MdOutlineDescription, MdSync } from "react-icons/md";

import "./settings.scss";
import "src/components/Details/details.scss";
import "src/pages/InvoicePage/invoicePage.scss";
import "src/components/Dunning/dunning.scss";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { IoArrowBack, IoChevronForward } from "react-icons/io5";
import { HiOutlineUser } from "react-icons/hi";
import { MdOutlineLocalHospital, MdAlternateEmail } from "react-icons/md";
import { TbFileEuro, TbAlertCircle, TbBuildingBank, TbFileSpreadsheet, TbTemplate, TbCreditCardPay, TbQuote } from "react-icons/tb";
import { MdOutlinePictureAsPdf } from "react-icons/md";
import { FaUsers } from "react-icons/fa";

import { SaveSpec, SaveButton } from "./SettingsControls";
import {
    DoctorSettings,
    PraxisSettings,
    PraxisRegistrationQr,
    BankSettings,
    InvoiceSettings,
    DunningSettings,
    FilePathSettings,
    TeamSettings,
    ZaalaSettings,
    SmtpSettings,
    GenericSettings,
    PdfSettings,
    FinApiConnectionSettings,
    SumupConnectionSettings,
    PostSettings,
    SepaSettings,
    DatevSettings,
    PraxisSoftwareSyncSettings,
    InvoiceTemplateSettings,
    JustificationSettings,
} from "./SettingsSections";
import PageHeader from "src/components/PageHeader/PageHeader";
import {
    isAdmin as checkAdmin,
    isStandard as checkStandard,
    isAccounting as checkAccounting,
    isDoctor as checkIsDoctor,
} from "src/utils/roles";
import { ChangePasswordSection } from "./ChangePasswordSection";
import { ResetMemberPasswordSection } from "./ResetMemberPasswordSection";

type NavItem = {
    key: string;
    label: string;
    icon: React.ElementType;
    allowedRoles?: number[];
    hiddenPlans?: number[];
    // Restricts: visible to allowedRoles UNLESS this is explicitly false.
    permKey?: "permDunning" | "permReports" | "permSettings";
    // Grants: visible outside allowedRoles too, but only when this is explicitly true —
    // for features that default to a narrower role (e.g. Admin-only) than the rest of the
    // section, where an override should ADD visibility rather than only take it away.
    permGrantKey?: "permDatev";
    // Extra condition on top of the above (false hides the item).
    when?: boolean;
};
type NavEntry = NavItem | { divider: true };

export default function Settings() {
    const { user, updateUser, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
    const userRole = user?.doctor?.userRights ?? 0;
    const isAdmin = checkAdmin(userRole);
    const isAccounting = checkAccounting(userRole);
    const isDoctor = checkIsDoctor(userRole);
    const [sepaIbanError, setSepaIbanError] = useState(false);
    const [bankIbanError, setBankIbanError] = useState(false);
    const businessPlan = user?.praxis?.businessPlan ?? 1;
    const canCreateInvoices = user?.doctor?.permEdit !== undefined ? !!user.doctor.permEdit : !checkStandard(userRole);
    const isTrialing = ["trialing", "pending_acceptance"].includes(user?.praxis?.subscriptionStatus ?? "");

    const NAV_ITEMS: NavEntry[] = [
        {
            key: "generic",
            label: t("settings.generic"),
            icon: MdOutlineLanguage,
        },
        { key: "profile", label: t("settings.myProfile"), icon: HiOutlineUser },
        { divider: true },
        {
            key: "bankpayments",
            label: t("settings.bankAndPayments"),
            icon: TbBuildingBank,
            // Doctor/Admin always (own account) — no permBank override, see canSeeBank.
            allowedRoles: [2, 3],
        },
        {
            // Card payment via SumUp (chapter 10) — own account per Doctor/Admin, praxis account
            // and the praxis-wide switches Admin-only. Same never-delegable rule as the bank link.
            key: "sumup",
            label: t("settings.cardPaymentSumup"),
            icon: TbCreditCardPay,
            allowedRoles: [2, 3],
        },
        {
            key: "praxis",
            label: t("settings.praxis"),
            icon: MdOutlineLocalHospital,
            allowedRoles: [3],
        },
        {
            key: "team",
            label: t("settings.teamMembers"),
            icon: FaUsers,
            allowedRoles: [1, 2, 3, 5],
            permKey: "permSettings",
        },
        { divider: true },
        { key: "invoices", label: t("settings.invoices"), icon: TbFileEuro, allowedRoles: [1, 2, 3] },
        // Templates for "Rechnung erstellen" (chapter 18) — for whoever can create invoices
        // (same rule as the wizard and requireNotStandard: permEdit, else not MFA/Accounting).
        { key: "invoice-templates", label: t("invoiceTemplates.settingsTitle"), icon: TbTemplate, when: canCreateInvoices },
        // Saved Begründungen for invoice lines (chapter 18) — same rule as templates.
        { key: "justifications", label: t("justifications.settingsTitle"), icon: TbQuote, when: canCreateInvoices },
        {
            key: "pdf",
            label: t("settings.pdfFormat"),
            icon: MdOutlinePictureAsPdf,
            // Own per doctor/admin now (like Bank & Payments) — MedicalAssistant/Accounting
            // don't have invoices billed under their own name, so there's no "own" for them
            // to configure here.
            allowedRoles: [2, 3],
        },
        { key: "dunning", label: t("settings.dunning"), icon: TbAlertCircle, allowedRoles: [1, 2, 3], permKey: "permDunning" },
        {
            key: "datev",
            label: t("settings.datev"),
            icon: TbFileSpreadsheet,
            allowedRoles: [3],
            permGrantKey: "permDatev",
        },
        {
            key: "praxis-software-sync",
            label: t("settings.praxisSoftwareSync"),
            icon: MdSync,
            // Includes MedicalAssistant (1) — front-desk staff are the ones most likely
            // to be at the machine when the practice software writes a new export, and
            // the confirm/results popups this feature shows are not role-gated, so they
            // need a way to reach the settings that explain and control it.
            allowedRoles: [1, 2, 3],
        },
        { divider: true },
        {
            key: "smtp",
            label: t("settings.email"),
            icon: MdAlternateEmail,
            allowedRoles: [3],
        },
        {
            key: "email-templates",
            label: t("settings.emailTemplates"),
            icon: MdOutlineMailOutline,
            allowedRoles: [3],
        },
        {
            key: "post",
            label: t("settings.post"),
            icon: MdOutlineLocalPostOffice,
            allowedRoles: [3],
        },
        {
            key: "post-templates",
            label: t("settings.tabLetterTemplates"),
            icon: MdOutlineDescription,
            allowedRoles: [3],
        },
        { divider: true },
        {
            key: "zaala",
            label: "About Medinvoice",
            icon: HiOutlineInformationCircle,
        },
    ];

    // SumUp's registered OAuth return address is ?section=bankpayments (it can't be changed at
    // SumUp) — a connect result arriving there belongs on the SumUp page.
    const sumupReturn = searchParams.get("section") === "bankpayments" && searchParams.has("state")
        && (searchParams.has("code") || searchParams.has("error"));
    const activeKey = sumupReturn
        ? "sumup"
        : searchParams.get("section") ??
          (location.state as any)?.section ??
          "generic";
    // SumupConnectionSettings (a child, so its effect runs first) has already read code/state —
    // move to the SumUp page without them so a reload can't replay the one-time code.
    useEffect(() => {
        if (sumupReturn) setSearchParams({ section: "sumup" }, { replace: true });
    }, [sumupReturn, setSearchParams]);

    // Portal target for the own Save button of Team Members and Kartenzahlung (SumUp), so it
    // renders in the same fixed-position breadcrumb slot every other subpage's Save button uses.
    const [teamSaveSlot, setTeamSaveSlot] = useState<HTMLDivElement | null>(null);
    const setActiveKey = (key: string) =>
        setSearchParams({ section: key }, { replace: true });

    // Single choke point every settings section passes through — lets the "Getting Started"
    // checklist count a setting as reviewed even when the user deliberately keeps its default.
    useEffect(() => {
        apiRequest("/onboarding/progress/visit", {
            method: "POST",
            body: JSON.stringify({ key: activeKey }),
        }, { "Content-Type": "application/json" }).catch(() => {});
    }, [activeKey]);

    const {
        data: doctors,
        loading: loadingDoctors,
        refetch: refetchDoctors,
    } = useApi<any[]>("/doctors", isAdmin ? 1 : 0); // only fetch team list for admins

    const {
        dunningDays,
        setDunningDays,
        praxis,
        setPraxis,
        doctor,
        setDoctor,
        sendMethods,
        toggleSendMethod,
        emailLinkModes,
        setEmailLinkModes,
        postSendOnUpload,
        setPostSendOnUpload,
        paidThreshold,
        setPaidThreshold,
        sendOnUpload,
        setSendOnUpload,
        sendOnQuestionnaireSubmit,
        setSendOnQuestionnaireSubmit,
        requireLegalAcceptance,
        setRequireLegalAcceptance,
        questionnairePostOption,
        setQuestionnairePostOption,
        questionsEnabled,
        setQuestionsEnabled,
        tagsEnabled,
        setTagsEnabled,
        hideCategoryLabels,
        setHideCategoryLabels,
        dunningLevelSendModes,
        setDunningLevelSendModes,
        dunningFallbackToLetter,
        setDunningFallbackToLetter,
        dunningLetterOptions,
        setDunningLetterOptions,
        dunningFinesEnabled,
        setDunningFinesEnabled,
        dunningFines,
        setDunningFines,
        bank,
        setBank,
        sepa,
        setSepa,
        bdtPath,
        setBdtPath,
        padPath,
        setPadPath,
        datev,
        setDatev,
        smtp,
        setSmtp,
        emailTexts,
        setEmailTexts,
        emailSubjects,
        setEmailSubjects,
        legalMode,
        setLegalMode,
        paywiseApiKey,
        setPaywiseApiKey,
        paywiseConfirmed,
        setPaywiseConfirmed,
        pdfSettings,
        setPdfSettings,
        pdfSettingsIsOwn,
        setPdfSettingsIsOwn,
        postColor,
        setPostColor,
        postDuplex,
        setPostDuplex,
        postDispatch,
        setPostDispatch,
        postEnvelope,
        setPostEnvelope,
        postShipping,
        setPostShipping,
        letterTexts,
        setLetterTexts,
        letterQrCodes,
        setLetterQrCodes,
        csvReportEnabled,        setCsvReportEnabled,
        csvReportEmails,         setCsvReportEmails,
        csvReportIntervalAmount, setCsvReportIntervalAmount,
        csvReportIntervalUnit,   setCsvReportIntervalUnit,
        csvReportFilter,         setCsvReportFilter,
        csvReportAmountMin,      setCsvReportAmountMin,
        saved,
        save,
        dirty,
        markDirty,
        clearDirty,
    } = useSettings();

    const visibleNav = useMemo(() => {
        const filtered = NAV_ITEMS.filter((item) => {
            if ("divider" in item) return true;
            // SEPA mandate is hidden separately inside bankpayments (isAdmin gate); show the section always
            return (
                (!item.allowedRoles || item.allowedRoles.includes(userRole) ||
                    (!!item.permGrantKey && user?.doctor?.[item.permGrantKey] === true)) &&
                (!item.hiddenPlans || !item.hiddenPlans.includes(businessPlan)) &&
                (!item.permKey || user?.doctor?.[item.permKey] !== false) &&
                item.when !== false
            );
        });
        // Remove leading, trailing, and consecutive dividers
        return filtered.filter((item, i, arr) => {
            if (!("divider" in item)) return true;
            const prev = arr[i - 1];
            const next = arr[i + 1];
            if (!prev || "divider" in prev) return false;
            if (!next || "divider" in next) return false;
            return true;
        });
    }, [userRole, businessPlan, canCreateInvoices, t]);

    const renderContent = () => {
        switch (activeKey) {
            case "profile":
                return (
                    <>
                        <DoctorSettings
                            doctor={doctor}
                            saved={!!saved["doctor"]}
                            dirty={!!dirty["doctor"]}
                            onChange={(v) => { setDoctor(v); markDirty("doctor"); }}
                            onDeleteAccount={async () => {
                                await apiRequest("/doctors/me", { method: "DELETE" });
                                logout();
                                navigate("/login");
                            }}
                            onSave={async () => {
                                const tel = doctor.phone
                                    ? doctor.phone
                                          .split(",")
                                          .map((p: string) => p.trim())
                                          .filter(Boolean)
                                    : [];
                                await save("doctor", {
                                    ...doctor,
                                    tel,
                                    phone: undefined,
                                });
                                updateUser({
                                    doctor: {
                                        ...user?.doctor,
                                        ...doctor,
                                        tel: tel.join(", "),
                                        id: user?.doctor?.id ?? "",
                                        userRights:
                                            user?.doctor?.userRights ?? 0,
                                        salutation: doctor.salutation,
                                        title: doctor.title,
                                        specialty: doctor.specialty,
                                    },
                                });
                            }}
                        />
                        <ChangePasswordSection />
                    </>
                );
            case "praxis":
                return (
                    <>
                        <PraxisSettings
                            praxis={praxis}
                            saved={!!saved["praxis"]}
                            dirty={!!dirty["praxis"]}
                            onChange={(v) => { setPraxis(v); markDirty("praxis"); }}
                            onSave={async () => {
                                await save("praxis", praxis);
                                updateUser({
                                    praxis: {
                                        ...user?.praxis,
                                        ...praxis,
                                        tel: praxis.tel.join(", "),
                                        id: user?.praxis?.id ?? "",
                                    },
                                });
                            }}
                        />
                        <PraxisRegistrationQr praxisName={praxis.name} />
                    </>
                );
            case "bankpayments":
                return (
                    <>
                        <BankSettings
                            bank={bank}
                            saved={!!saved["bank"]}
                            dirty={!!dirty["bank"] && !sepaIbanError}
                            onChange={(v) => { setBank(v); markDirty("bank"); }}
                            description={t("settings.bankDetailsDesc")}
                            onIbanError={setBankIbanError}
                            fallbackIban={sepa.iban}
                            fallbackBic={sepa.bic}
                            onSave={async () => {
                                if (sepaIbanError) return;
                                await save("bank", bank);
                                updateUser({
                                    doctor: {
                                        ...user?.doctor,
                                        id: user?.doctor?.id ?? "",
                                        userRights:
                                            user?.doctor?.userRights ?? 0,
                                        firstName:
                                            user?.doctor?.firstName ?? "",
                                        lastName: user?.doctor?.lastName ?? "",
                                        iban: bank.iban,
                                        bic: bank.bic,
                                        taxNumber: bank.taxNumber,
                                        scanPayName: bank.scanPayName,
                                    },
                                });
                                if (isAdmin) {
                                    try {
                                        await apiRequest(
                                            "/sepa/mandate",
                                            {
                                                method: "PATCH",
                                                body: JSON.stringify(sepa),
                                            },
                                            {
                                                "Content-Type":
                                                    "application/json",
                                            },
                                        );
                                        updateUser({
                                            praxis: {
                                                ...user?.praxis,
                                                iban: sepa.iban
                                                    .replace(/\s/g, "")
                                                    .toUpperCase(),
                                                bic: sepa.bic.toUpperCase(),
                                                id: user?.praxis?.id ?? "",
                                                name: user?.praxis?.name ?? "",
                                            },
                                        });
                                    } catch (e: any) {
                                        // 404 = no mandate exists yet for this praxis — silent skip
                                        if (e?.status !== 404) {
                                            console.error("Failed to update SEPA mandate", e);
                                        }
                                    }
                                }
                            }}
                        />
                        {(() => {
                            const role = user?.doctor?.userRights;
                            if (!canSeeBankFor(user?.doctor)) return null;
                            // Connect/disconnect/sync are never delegable (requireBankManage on
                            // the backend has no override) — Doctor/Admin only, same as Bank.tsx's
                            // own canManageBank. A View-only grant gets a note instead of a form
                            // whose submit would just 403.
                            const canManageBank = role === 2 || role === 3;
                            return canManageBank
                                ? <FinApiConnectionSettings />
                                : <p className="settings-info-banner__highlight">{t("settings.bankManageViewOnly")}</p>;
                        })()}
                        {isAdmin && (
                            <SepaSettings
                                sepa={sepa}
                                onChange={(v) => { setSepa(v); markDirty("bank"); }}
                                onIbanError={setSepaIbanError}
                                description={t("settings.sepaPaymentDesc")}
                            />
                        )}
                    </>
                );
            case "sumup":
                return <SumupConnectionSettings saveButtonPortalTarget={teamSaveSlot} />;
            case "invoices":
                return (
                    <fieldset disabled={isDoctor} style={{ border: "none", padding: 0, margin: 0 }}>
                        <InvoiceSettings
                            paidThreshold={paidThreshold}
                            sendMethods={sendMethods}
                            sendOnUpload={sendOnUpload}
                            sendOnQuestionnaireSubmit={sendOnQuestionnaireSubmit}
                            requireLegalAcceptance={requireLegalAcceptance}
                            questionnairePostOption={questionnairePostOption}
                            emailLinkModes={emailLinkModes}
                            questionsEnabled={questionsEnabled}
                            tagsEnabled={tagsEnabled}
                            hideCategoryLabels={hideCategoryLabels}
                            saved={!!saved["invoice"]}
                            dirty={!!dirty["invoice"]}
                            onPaidThresholdChange={(v) => { setPaidThreshold(v); markDirty("invoice"); }}
                            onToggleEmail={() => { toggleSendMethod("email"); markDirty("invoice"); }}
                            onEmailLinkModesChange={(v) => { setEmailLinkModes(v); markDirty("invoice"); }}
                            onSendOnUploadChange={(v) => { setSendOnUpload(v); markDirty("invoice"); }}
                            onSendOnQuestionnaireSubmitChange={(v) => { setSendOnQuestionnaireSubmit(v); markDirty("invoice"); }}
                            onRequireLegalAcceptanceChange={(v) => { setRequireLegalAcceptance(v); markDirty("invoice"); }}
                            onQuestionnairePostOptionChange={(v) => { setQuestionnairePostOption(v); markDirty("invoice"); }}
                            onQuestionsEnabledChange={(v) => { setQuestionsEnabled(v); markDirty("invoice"); }}
                            onTagsEnabledChange={(v) => { setTagsEnabled(v); markDirty("invoice"); }}
                            onHideCategoryLabelsChange={(v) => { setHideCategoryLabels(v); markDirty("invoice"); }}
                            csvReportEnabled={csvReportEnabled}
                            csvReportEmails={csvReportEmails}
                            csvReportIntervalAmount={csvReportIntervalAmount}
                            csvReportIntervalUnit={csvReportIntervalUnit}
                            csvReportFilter={csvReportFilter}
                            csvReportAmountMin={csvReportAmountMin}
                            onCsvReportChange={(patch) => {
                                if (patch.csvReportEnabled        !== undefined) setCsvReportEnabled(patch.csvReportEnabled);
                                if (patch.csvReportEmails         !== undefined) setCsvReportEmails(patch.csvReportEmails);
                                if (patch.csvReportIntervalAmount !== undefined) setCsvReportIntervalAmount(patch.csvReportIntervalAmount);
                                if (patch.csvReportIntervalUnit   !== undefined) setCsvReportIntervalUnit(patch.csvReportIntervalUnit);
                                if (patch.csvReportFilter         !== undefined) setCsvReportFilter(patch.csvReportFilter);
                                if (patch.csvReportAmountMin      !== undefined) setCsvReportAmountMin(patch.csvReportAmountMin);
                                markDirty("invoice");
                            }}
                            onSave={() =>
                                save("invoice", {
                                    paidThreshold,
                                    sendMethods,
                                    sendOnUpload,
                                    sendOnQuestionnaireSubmit,
                                    requireLegalAcceptance,
                                    questionnairePostOption,
                                    emailLinkModes,
                                    questionsEnabled,
                                    tagsEnabled,
                                    hideCategoryLabels,
                                    csvReportEnabled,
                                    csvReportEmails,
                                    csvReportIntervalAmount,
                                    csvReportIntervalUnit,
                                    csvReportFilter,
                                    csvReportAmountMin,
                                })
                            }
                        />
                    </fieldset>
                );
            case "pdf":
                return (
                    <PdfSettings
                        pdfSettings={pdfSettings}
                        pdfSettingsIsOwn={pdfSettingsIsOwn}
                        hideCategoryLabels={hideCategoryLabels}
                        saved={!!saved["pdf"]}
                        dirty={!!dirty["pdf"]}
                        onChange={(v) => { setPdfSettings(v); markDirty("pdf"); }}
                        onSave={async () => {
                            const ok = await save("pdf", { pdfSettings });
                            if (ok) setPdfSettingsIsOwn(true);
                        }}
                        onClear={() => setPdfSettings(DEFAULT_PDF_SETTINGS)}
                    />
                );
            case "dunning":
                return (
                    <fieldset disabled={isDoctor} style={{ border: "none", padding: 0, margin: 0 }}>
                        <DunningSettings
                            days={dunningDays}
                            legalMode={legalMode}
                            paywiseApiKey={paywiseApiKey}
                            onPaywiseApiKeyChange={(v) => { setPaywiseApiKey(v); markDirty("dunning"); }}
                            paywiseConfirmed={paywiseConfirmed}
                            onPaywiseConfirmedChange={(v) => { setPaywiseConfirmed(v); markDirty("dunning"); }}
                            dunningLevelSendModes={dunningLevelSendModes}
                            dunningFallbackToLetter={dunningFallbackToLetter}
                            dunningLetterOptions={dunningLetterOptions}
                            dunningFinesEnabled={dunningFinesEnabled}
                            dunningFines={dunningFines}
                            saved={!!saved["dunning"]}
                            dirty={!!dirty["dunning"]}
                            onChange={(v) => { setDunningDays(v); markDirty("dunning"); }}
                            onLegalModeChange={(v) => { setLegalMode(v); markDirty("dunning"); }}
                            onDunningLevelSendModes={(v) => { setDunningLevelSendModes(v); markDirty("dunning"); }}
                            onDunningFallbackToLetter={(v) => { setDunningFallbackToLetter(v); markDirty("dunning"); }}
                            onDunningLetterOptions={(v) => { setDunningLetterOptions(v); markDirty("dunning"); }}
                            onDunningFinesEnabled={(v) => { setDunningFinesEnabled(v); markDirty("dunning"); }}
                            onDunningFines={(v) => { setDunningFines(v); markDirty("dunning"); }}
                            onSave={() => {
                                const relativeIntervals = [
                                    0,
                                    ...dunningDays
                                        .slice(1)
                                        .map((d, i) => d - dunningDays[i]),
                                ];
                                save("dunning", {
                                    intervals: relativeIntervals,
                                    legalMode,
                                    paywiseApiKey,
                                    dunningLevelSendModes,
                                    dunningFallbackToLetter,
                                    dunningLetterOptions,
                                    dunningFinesEnabled,
                                    dunningFines,
                                });
                            }}
                        />
                    </fieldset>
                );
            case "datev":
                return (
                    <DatevSettings
                        datev={datev}
                        saved={!!saved["datev"]}
                        dirty={!!dirty["datev"]}
                        onChange={(v) => { setDatev(v); markDirty("datev"); }}
                        onSave={() => save("datev", datev)}
                    />
                );
            case "praxis-software-sync":
                return <PraxisSoftwareSyncSettings />;
            case "invoice-templates":
                return canCreateInvoices ? <InvoiceTemplateSettings /> : null;
            case "justifications":
                return canCreateInvoices ? <JustificationSettings /> : null;
            case "files":
                return (
                    <FilePathSettings
                        bdtPath={bdtPath}
                        padPath={padPath}
                        saved={!!saved["paths"]}
                        dirty={!!dirty["paths"]}
                        onBdtChange={(v) => { setBdtPath(v); markDirty("paths"); }}
                        onPadChange={(v) => { setPadPath(v); markDirty("paths"); }}
                        onSave={() => save("paths", { bdtPath, padPath })}
                    />
                );
            case "smtp":
                return (
                    <SmtpSettings
                        smtp={smtp}
                        emailTexts={emailTexts}
                        emailSubjects={emailSubjects}
                        dunningFinesEnabled={dunningFinesEnabled}
                        dunningFines={dunningFines}
                        dunningDays={dunningDays}
                        saved={!!saved["smtp"]}
                        dirty={!!dirty["smtp"]}
                        view="smtp"
                        onChange={(v) => { setSmtp(v); markDirty("smtp"); }}
                        onEmailTextsChange={(v) => { setEmailTexts(v); markDirty("smtp"); }}
                        onEmailSubjectsChange={(v) => { setEmailSubjects(v); markDirty("smtp"); }}
                        onSave={() =>
                            save("smtp", { ...smtp, emailTexts, emailSubjects })
                        }
                    />
                );
            case "email-templates":
                return (
                    <SmtpSettings
                        smtp={smtp}
                        emailTexts={emailTexts}
                        emailSubjects={emailSubjects}
                        dunningFinesEnabled={dunningFinesEnabled}
                        dunningFines={dunningFines}
                        dunningDays={dunningDays}
                        saved={!!saved["smtp"]}
                        dirty={!!dirty["smtp"]}
                        view="templates"
                        onChange={(v) => { setSmtp(v); markDirty("smtp"); }}
                        onEmailTextsChange={(v) => { setEmailTexts(v); markDirty("smtp"); }}
                        onEmailSubjectsChange={(v) => { setEmailSubjects(v); markDirty("smtp"); }}
                        onSave={() =>
                            save("smtp", { ...smtp, emailTexts, emailSubjects })
                        }
                    />
                );
            case "post":
                return (
                    <PostSettings
                        view="post"
                        sendMethods={sendMethods}
                        postColor={postColor}
                        postDuplex={postDuplex}
                        postDispatch={postDispatch}
                        postEnvelope={postEnvelope}
                        postShipping={postShipping}
                        postSendOnUpload={postSendOnUpload}
                        letterTexts={letterTexts}
                        pdfSettings={pdfSettings}
                        dunningFinesEnabled={dunningFinesEnabled}
                        dunningFines={dunningFines}
                        dunningDays={dunningDays}
                        saved={!!saved["post"]}
                        dirty={!!dirty["post"]}
                        onTogglePost={() => { toggleSendMethod("post"); markDirty("post"); }}
                        onColorChange={(v) => { setPostColor(v); markDirty("post"); }}
                        onDuplexChange={(v) => { setPostDuplex(v); markDirty("post"); }}
                        onDispatchChange={(v) => { setPostDispatch(v); markDirty("post"); }}
                        onEnvelopeChange={(v) => { setPostEnvelope(v); markDirty("post"); }}
                        onShippingChange={(v) => { setPostShipping(v); markDirty("post"); }}
                        onPostSendOnUploadChange={(v) => { setPostSendOnUpload(v); markDirty("post"); }}
                        onLetterTextsChange={(v) => { setLetterTexts(v); markDirty("post"); }}
                        onSave={() =>
                            save("post", {
                                sendMethods,
                                postColor,
                                postDuplex,
                                postDispatch,
                                postEnvelope,
                                postShipping,
                                postSendOnUpload,
                                letterTexts,
                                letterQrCodes,
                            })
                        }
                    />
                );
            case "post-templates":
                return (
                    <PostSettings
                        view="templates"
                        sendMethods={sendMethods}
                        postColor={postColor}
                        postDuplex={postDuplex}
                        postDispatch={postDispatch}
                        postEnvelope={postEnvelope}
                        postShipping={postShipping}
                        postSendOnUpload={postSendOnUpload}
                        letterTexts={letterTexts}
                        pdfSettings={pdfSettings}
                        dunningFinesEnabled={dunningFinesEnabled}
                        dunningFines={dunningFines}
                        dunningDays={dunningDays}
                        saved={!!saved["post"]}
                        dirty={!!dirty["post"]}
                        onTogglePost={() => { toggleSendMethod("post"); markDirty("post"); }}
                        onColorChange={(v) => { setPostColor(v); markDirty("post"); }}
                        onDuplexChange={(v) => { setPostDuplex(v); markDirty("post"); }}
                        onDispatchChange={(v) => { setPostDispatch(v); markDirty("post"); }}
                        onEnvelopeChange={(v) => { setPostEnvelope(v); markDirty("post"); }}
                        onShippingChange={(v) => { setPostShipping(v); markDirty("post"); }}
                        onPostSendOnUploadChange={(v) => { setPostSendOnUpload(v); markDirty("post"); }}
                        onLetterTextsChange={(v) => { setLetterTexts(v); markDirty("post"); }}
                        onSave={() =>
                            save("post", {
                                sendMethods,
                                postColor,
                                postDuplex,
                                postDispatch,
                                postEnvelope,
                                postShipping,
                                postSendOnUpload,
                                letterTexts,
                                letterQrCodes,
                            })
                        }
                    />
                );
            case "zaala":
                return <ZaalaSettings />;
            case "generic":
                return (
                    <fieldset disabled={isDoctor} style={{ border: "none", padding: 0, margin: 0 }}>
                        <GenericSettings showReports={businessPlan >= 3} minimal={isAccounting} onSaveRef={genericSaveRef} onDirty={markDirty} onSaved={clearDirty} />
                    </fieldset>
                );
            case "team":
                return (
                    <>
                        <TeamSettings
                            doctors={doctors}
                            loading={loadingDoctors}
                            currentDoctorId={user?.doctor?.id ?? ""}
                            saveButtonPortalTarget={teamSaveSlot}
                            onRoleChange={async (id, userRights) => {
                                await save(`doctor-role/${id}`, { userRights });
                                refetchDoctors();
                            }}
                            onPermChange={async (id, perms) => {
                                await save(`doctor-permissions/${id}`, perms);
                                refetchDoctors();
                            }}
                            onDelete={async (id, mode) => {
                                await apiRequest(`/doctors/${id}?mode=${mode}`, {
                                    method: "DELETE",
                                });
                                refetchDoctors();
                            }}
                            onCancelDeletion={async (id) => {
                                await apiRequest(`/doctors/${id}/cancel-deletion`, {
                                    method: "POST",
                                });
                                refetchDoctors();
                            }}
                            onAdd={async (data) => {
                                await apiRequest(
                                    "/doctors",
                                    {
                                        method: "POST",
                                        body: JSON.stringify({
                                            praxis: [user?.praxis?.id],
                                            ...data,
                                        }),
                                    },
                                    { "Content-Type": "application/json" },
                                );
                                refetchDoctors();
                            }}
                            onEdit={async (id, data) => {
                                await apiRequest(
                                    "/doctors",
                                    {
                                        method: "POST",
                                        body: JSON.stringify({ id, ...data }),
                                    },
                                    { "Content-Type": "application/json" },
                                );
                                refetchDoctors();
                            }}
                        />
                        <ResetMemberPasswordSection
                            doctors={doctors ?? null}
                            currentDoctorId={user?.doctor?.id ?? ""}
                        />
                    </>
                );
            default:
                return <GenericSettings showReports={businessPlan >= 3} onSaveRef={genericSaveRef} onDirty={markDirty} onSaved={clearDirty} />;
        }
    };

    const activeNavItem = visibleNav.find(
        (n): n is NavItem => !("divider" in n) && n.key === activeKey,
    );
    const activeLabel = activeNavItem?.label ?? "";

    // Ref holds the generic-tab save (includes i18n/language change that lives in GenericSettings)
    const genericSaveRef = useRef<() => void>(() => {});

    // All save handlers, refreshed every render so they always close over latest state
    const saveHandlersRef = useRef<Record<string, () => void>>({});
    saveHandlersRef.current = {
        profile: async () => {
            const tel = doctor.phone ? doctor.phone.split(",").map((p: string) => p.trim()).filter(Boolean) : [];
            await save("doctor", { ...doctor, tel, phone: undefined });
            updateUser({ doctor: { ...user?.doctor, ...doctor, tel: tel.join(", "), id: user?.doctor?.id ?? "", userRights: user?.doctor?.userRights ?? 0, salutation: doctor.salutation, title: doctor.title, specialty: doctor.specialty } });
        },
        praxis: async () => {
            await save("praxis", praxis);
            updateUser({ praxis: { ...user?.praxis, ...praxis, tel: praxis.tel.join(", "), id: user?.praxis?.id ?? "" } });
        },
        bankpayments: async () => {
            if (bankIbanError || sepaIbanError) return;
            await save("bank", bank);
            updateUser({ doctor: { ...user?.doctor, id: user?.doctor?.id ?? "", userRights: user?.doctor?.userRights ?? 0, firstName: user?.doctor?.firstName ?? "", lastName: user?.doctor?.lastName ?? "", iban: bank.iban, bic: bank.bic, taxNumber: bank.taxNumber, scanPayName: bank.scanPayName } });
            if (isAdmin) {
                try {
                    await apiRequest("/sepa/mandate", { method: "PATCH", body: JSON.stringify(sepa) }, { "Content-Type": "application/json" });
                    updateUser({ praxis: { ...user?.praxis, iban: sepa.iban.replace(/\s/g, "").toUpperCase(), bic: sepa.bic.toUpperCase(), id: user?.praxis?.id ?? "", name: user?.praxis?.name ?? "" } });
                } catch (e: any) { if (e?.status !== 404) console.error("Failed to update SEPA mandate", e); }
            }
        },
        invoices: () => save("invoice", { paidThreshold, sendMethods, sendOnUpload, sendOnQuestionnaireSubmit, requireLegalAcceptance, questionnairePostOption, emailLinkModes, questionsEnabled, tagsEnabled, hideCategoryLabels, csvReportEnabled, csvReportEmails, csvReportIntervalAmount, csvReportIntervalUnit, csvReportFilter, csvReportAmountMin }),
        pdf:      async () => {
            const ok = await save("pdf", { pdfSettings });
            if (ok) setPdfSettingsIsOwn(true);
        },
        dunning:  () => {
            // A field can still hold an unblurred, not-yet-clamped value if Save fires without
            // leaving the input (e.g. Enter key) — normalize the whole array here so every level
            // is guaranteed strictly later than the previous one before it's turned into deltas
            // (dunningCron sums these to get each level's cumulative day count; a zero/negative
            // gap would let a later level fire on or before an earlier one).
            const normalizedDays = dunningDays.reduce((acc: number[], d: number, i: number) =>
                i === 0 ? [d] : [...acc, Math.max(d, acc[i - 1] + 1)], [] as number[]);
            if (normalizedDays.some((d, i) => d !== dunningDays[i])) setDunningDays(normalizedDays);
            const relativeIntervals = [0, ...normalizedDays.slice(1).map((d: number, i: number) => d - normalizedDays[i])];
            save("dunning", { intervals: relativeIntervals, legalMode, paywiseApiKey, paywiseConfirmed, dunningLevelSendModes, dunningFallbackToLetter, dunningLetterOptions, dunningFinesEnabled, dunningFines });
        },
        files:    () => save("paths", { bdtPath, padPath }),
        datev:    () => save("datev", datev),
        smtp:     () => save("smtp", { ...smtp, emailTexts, emailSubjects }),
        "email-templates": () => save("smtp", { ...smtp, emailTexts, emailSubjects }),
        post:     () => save("post", { sendMethods, postColor, postDuplex, postDispatch, postEnvelope, postShipping, postSendOnUpload, letterTexts, letterQrCodes }),
        "post-templates": () => save("post", { sendMethods, postColor, postDuplex, postDispatch, postEnvelope, postShipping, postSendOnUpload, letterTexts, letterQrCodes }),
        generic:  () => genericSaveRef.current(),
    };

    const SAVE_KEY: Record<string, string> = {
        profile: "doctor", praxis: "praxis", bankpayments: "bank",
        invoices: "invoice", pdf: "pdf", dunning: "dunning", datev: "datev",
        files: "paths", smtp: "smtp", "email-templates": "smtp",
        post: "post", "post-templates": "post", generic: "generic",
    };
    const activeSaveKey = SAVE_KEY[activeKey];
    const activeSave: SaveSpec | null = activeSaveKey ? {
        onSave: () => saveHandlersRef.current[activeKey]?.(),
        saved:  !!saved[activeSaveKey],
        dirty:  activeKey === "bankpayments" ? !!dirty["bank"] && !bankIbanError && !sepaIbanError
              : activeKey === "generic"      ? !!dirty["generic"] || !!dirty["reports"]
              : !!dirty[activeSaveKey],
    } : null;

    return (
        <>
        <div className="detail-breadcrumb detail-breadcrumb--settings">
            <button className="btn-back" onClick={() => navigate(-1)}>
                <IoArrowBack /> {t("settings.back")}
            </button>
            <span
                className="detail-breadcrumb__parent"
                onClick={() => navigate(-1)}
            >
                {t("settings.title")}
            </span>
            <IoChevronForward className="detail-breadcrumb__sep" />
            <span className="detail-breadcrumb__current">
                {activeLabel}
            </span>
            {activeSave && (
                <div className="detail-breadcrumb__actions">
                    <SaveButton onClick={activeSave.onSave} saved={activeSave.saved} dirty={activeSave.dirty} />
                </div>
            )}
            {(activeKey === "team" || activeKey === "sumup") && (
                <div className="detail-breadcrumb__actions" ref={setTeamSaveSlot} />
            )}
        </div>
        <div className="page settings-page">
            <PageHeader
                eyebrow={t("settings.title")}
                title={t("settings.title")}
                lead={t("settings.pageLead")}
                accentColor="#64748b"
            />

            <div className="settings-layout">
                {/* Sidebar */}
                <nav className="settings-nav">
                    {visibleNav.map((item, i) =>
                        "divider" in item ? (
                            <hr
                                key={`div-${i}`}
                                className="settings-nav__divider"
                            />
                        ) : (
                            <button
                                key={item.key}
                                className={`settings-nav__item ${activeKey === item.key ? "settings-nav__item--active" : ""}`}
                                onClick={() => setActiveKey(item.key)}
                            >
                                <item.icon className="settings-nav__icon" />
                                {item.label}
                            </button>
                        ),
                    )}
                </nav>

                {/* Content */}
                <div className="settings-content">{renderContent()}</div>
            </div>
        </div>
        </>
    );
}
